import React, { useState, useRef, useEffect } from "react";
import { Box } from "@vapor/v3-components";
import { isVideo } from "../utils/util";

interface CardDocumentProps {
  index: number;
  documentType: string; //"LINK" | "YOUTUBE" | "VIDEO" | "NONE";
  documentUrl: string;
  documentPreview: string;
  noDownloadVideo: boolean;
  onClick: (index: number) => void;
}

export const CardDocument: React.FC<CardDocumentProps> = ({
  index,
  documentType,
  documentUrl,
  documentPreview,
  noDownloadVideo,
  onClick,
}) => {
  const docType =
    documentType === "LINK" || documentType === "YOUTUBE"
      ? documentType
      : isVideo(documentType)
      ? "VIDEO"
      : "NONE";

  const [isHovered, setIsHovered] = useState(
    docType === "VIDEO" || docType === "LINK" || docType === "YOUTUBE"
      ? true
      : false
  );
  const elementRef = useRef<HTMLImageElement | null>(null);
  const [height, setHeight] = useState<number | null>(null);
  const [width, setWidth] = useState<number | null>(null);
  const [youtubeUrl, setYoutubeUrl] = useState<string>("");

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.contentRect.height > 0) setHeight(entry.contentRect.height);
        if (entry.contentRect.width > 0) setWidth(entry.contentRect.width);
      }
    });

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    if (docType === "YOUTUBE") setIsHovered(false);
    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
    };
  }, []);

  const handleMouseOverImg = (
    event: React.MouseEvent<HTMLImageElement, MouseEvent>
  ) => {
    if (docType === "YOUTUBE") {
      setYoutubeUrl(documentUrl + "?autoplay=1");
      setIsHovered(true);
    }
  };

  const handleMouseEnter = (
    event: React.MouseEvent<HTMLVideoElement, MouseEvent>
  ) => {
    const video = event.currentTarget;
    video.play().catch((error) => {
      console.error("Error during play:", error);
    });
  };

  const handleMouseLeave = (
    event: React.MouseEvent<HTMLVideoElement, MouseEvent>
  ) => {
    const video = event.currentTarget;
    video.pause();
  };

  const handleMouseOut = (
    event: React.MouseEvent<HTMLIFrameElement, MouseEvent>
  ) => {
    setIsHovered(false);
    setYoutubeUrl("");
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center", // Centrare il video all'interno del box
        padding: 0,
        height: "170px", // Altezza del contenitore
        width: "170px", // Larghezza del contenitore
        overflow: "hidden", // Nasconde il contenuto che esce dai bordi
        backgroundColor: "#f0f0f0", // Sfondo per visualizzare i limiti del contenitore
      }}
    >
      {(docType === "NONE" || docType === "YOUTUBE") && (
        <img
          ref={elementRef}
          src={documentPreview}
          alt="Preview"
          style={{
            backgroundColor: "#0055b86522, 100%, 36.07843137254902%)",
            width: "100%", // Larghezza massima
            height: "100%", // Altezza massima
            objectFit: "contain", // Mantiene le proporzioni, senza tagliare nulla
            display: `${
              (docType === "YOUTUBE" || docType === "NONE") && !isHovered
                ? "inherit"
                : "none"
            }`,
          }}
          onMouseOver={handleMouseOverImg}
          onClick={() => {
            onClick(index);
          }}
        />
      )}
      {(docType === "VIDEO" || docType === "LINK") && (
        <video
          src={documentUrl}
          style={{
            backgroundColor: "#0055b86522, 100%, 36.07843137254902%)",
            width: "100%", // Larghezza massima
            height: "100%", // Altezza massima
            objectFit: "contain", // Mantiene le proporzioni, senza tagliare nulla
          }}
          muted
          loop
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={() => {
            onClick(index);
          }}
          controls
          controlsList={noDownloadVideo ? "nodownload" : ""}
          poster={documentPreview}
        />
      )}
      {docType === "YOUTUBE" && (
        // eslint-disable-next-line jsx-a11y/iframe-has-title
        <iframe
          id={"iFrame"}
          src={youtubeUrl}
          style={{
            backgroundColor: "#0055b86522, 100%, 36.07843137254902%)",
            width: `${width}px`,
            height: `${height}px`,
            display: `${isHovered ? "inherit" : "none"}`,
          }}
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          onMouseOut={handleMouseOut}
          onClick={() => {
            onClick(index);
          }}
        />
      )}
    </Box>
  );
};
